import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext(null);
const AUTH_KEY = 'vwatch_admin_auth';

const getStoredAuth = () => {
  try {
    const raw = sessionStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) { console.warn('Auth parse error:', e.message); return null; }
};

const storeAuth = (data) => {
  try { sessionStorage.setItem(AUTH_KEY, JSON.stringify(data)); }
  catch (e) { console.error('Auth storage error:', e.message); }
};

const clearAuth = () => {
  try { sessionStorage.removeItem(AUTH_KEY); }
  catch (e) { console.warn('Auth clear error:', e.message); }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = getStoredAuth();
    if (stored?.isAuthenticated && stored?.user) {
      setUser(stored.user);
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const token = response.access_token || response.token;
      if (!token) throw new Error('No token received');
      storeAuth({ isAuthenticated: true, token, user: null });
      const me = await authAPI.getCurrentUser();
      const userData = me || { email, name: email.split('@')[0], role: 'admin' };
      storeAuth({ isAuthenticated: true, token, user: userData });
      setUser(userData);
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error.message);
      clearAuth();
      return { success: false, error: error.message || 'Login failed' };
    }
  }, []);

  const logout = useCallback(() => { clearAuth(); setUser(null); }, []);

  const value = useMemo(() => ({
    user, login, logout, isAuthenticated: !!user, loading
  }), [user, login, logout, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
